import { ArcElement, Plugin, Tooltip, TooltipPositionerFunction, ChartType } from 'chart.js';
import { DiagnosticsStatusByClassReportQuery$data } from '../../../../graphQLOperations/__generated__/DiagnosticsStatusByClassReportQuery.graphql';
import { splitId } from '../../../../businessLogicService/splitIdsFromBackEnd';
import { DiagnosticsDemonstratedSkillsQuery$data } from '../../../../graphQLOperations/__generated__/DiagnosticsDemonstratedSkillsQuery.graphql';
import { getStudentLastName } from '../../../CloserLook/closerLookHelper';

export enum DiagnosticStatus {
  COMPLETED = 'completed',
  IN_PROGRESS = 'in-progress',
  NOT_STARTED = 'not-started',
}
export interface DismissedStudent {
  studentId: string;
  diagnosticId: string | null;
}

/**
 * Decides the percentage label's text color based on the slice's background color.
 *
 * Computes the perceived luminance of the background color (hex) and returns dark
 * text over light backgrounds (e.g. the "Not started" gray #D3D3D3) and white text
 * over dark/saturated backgrounds (Completed #42C5A6, In-progress #5776FC), so every
 * label keeps good contrast without depending on specific hex values.
 *
 * @param sliceColor the slice's background color in hex (e.g. '#D3D3D3')
 * @returns the CSS color for the percentage text
 */
const getLabelTextColor = (sliceColor: string): string => {
  const hex = sliceColor.replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

  return luminance > 128 ? '#5A5A5A' : '#FFFFFF';
};

/**
 * Chart.js plugin that draws the percentage (e.g. "60%") centered on each
 * pie chart slice, matching the requested design.
 */
export const percentageLabelsPlugin: Plugin<'pie', { textColor?: string }> = {
  id: 'percentageLabels',
  afterDraw(chart, _args, options) {
    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);
    const dataset = chart.data.datasets[0];
    const values = dataset.data as number[];
    const total = values.reduce((sum, value) => sum + value, 0);

    if (!total) return;

    ctx.save();
    ctx.font = 'bold 18px "Atkinson Hyperlegible", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    meta.data.forEach((arc, index) => {
      const value = values[index];
      if (!value) return;

      const percentage = Math.round((value / total) * 100);
      // A full-circle slice has no meaningful centroid — tooltipPosition() would
      // push the label off toward the bisector. Anchor it at the pie's center instead.
      const { x, y } =
        value === total ? (arc.getProps(['x', 'y'], true) as { x: number; y: number }) : arc.tooltipPosition(true);
      const backgroundColors = dataset.backgroundColor as string[];

      ctx.fillStyle = options?.textColor ?? getLabelTextColor(backgroundColors[index]);
      ctx.fillText(`${percentage}%`, x, y);
    });

    ctx.restore();
  },
};

/**
 * How far *past the slice's outer edge* the tooltip anchor (caret tip) is pushed,
 * in px. Bigger = the tooltip floats further away from the pie.
 */
const TOOLTIP_OUTSIDE_OFFSET = 4;

/**
 * How aligned with an axis the slice's bisector must be before we snap the
 * tooltip body straight out along that axis (vs. letting it sit diagonally).
 * cos/sin range is [-1, 1]; 0.4 ≈ within ~24° of the axis.
 */
const TOOLTIP_ALIGN_DEADZONE = 0.4;

// Registers `outsideSlice` as a valid value for `options.plugins.tooltip.position`.
declare module 'chart.js' {
  interface TooltipPositionerMap {
    outsideSlice: TooltipPositionerFunction<ChartType>;
  }
}

/**
 * Custom tooltip positioner: anchors the tooltip *outside* the hovered slice
 * instead of at its centroid (the default `'average'` positioner), so it never
 * overlaps the percentage label drawn by `percentageLabelsPlugin`.
 *
 * The anchor (caret tip) sits on the slice's bisector angle, just past its outer
 * radius, and we return `xAlign`/`yAlign` so Chart.js renders the tooltip *body*
 * on the outward side of that anchor (away from the pie center) rather than
 * flipping it back over the slice. Note Chart.js alignment is worded from the
 * caret's point of view, so it reads inverted: `yAlign: 'top'` draws the body
 * *below* the caret, `xAlign: 'left'` draws it to the *right*, and so on.
 *
 * The pie needs empty room around it to land in — see the `layout.padding` on
 * the chart options in TeacherDiagnosticDashboardChartWrapper.
 *
 * Near-horizontal (east/west) slices are the exception. There the bisector is
 * within the align dead-zone, so the body would be pushed straight out sideways —
 * and it's much wider than it is tall (e.g. "2 students"), needing ~90px of
 * clearance, far more than the layout padding. Chart.js then clamps the body back
 * inside the canvas (getBackgroundPoint → _limitValue), dropping it right on top
 * of the percentage label. For just that band we route the tooltip *vertically*
 * instead — centered over the pie, dropped into the top/bottom padding where the
 * short side fits — which clears the label without any clamping. Diagonal and
 * near-vertical slices keep the original bisector placement.
 */
Tooltip.positioners.outsideSlice = function (elements) {
  if (!elements.length) return false;

  const arc = elements[0].element as ArcElement;
  const { x, y, startAngle, endAngle, outerRadius } = arc.getProps(
    ['x', 'y', 'startAngle', 'endAngle', 'outerRadius'],
    true,
  );

  // Polar → cartesian: walk from the pie's center along the slice's bisector
  // angle, out past the rim, so the tooltip clears the on-slice percentage label.
  const midAngle = (startAngle + endAngle) / 2;
  const distance = outerRadius + TOOLTIP_OUTSIDE_OFFSET;
  const cos = Math.cos(midAngle);
  const sin = Math.sin(midAngle);

  // A wide tooltip can't clear a near-horizontal slice sideways (it would be
  // clamped over the label), so send it up/down over the pie's center instead.
  // A dead-flat bisector (sin ~ 0) falls to the bottom, which is always clear.
  if (Math.abs(sin) < TOOLTIP_ALIGN_DEADZONE) {
    const goDown = sin >= 0;
    return {
      x,
      y: goDown ? y + distance : y - distance,
      xAlign: 'center',
      yAlign: goDown ? 'top' : 'bottom',
    };
  }

  return {
    x: x + cos * distance,
    y: y + sin * distance,
    xAlign: cos > TOOLTIP_ALIGN_DEADZONE ? 'left' : cos < -TOOLTIP_ALIGN_DEADZONE ? 'right' : 'center',
    yAlign: sin > TOOLTIP_ALIGN_DEADZONE ? 'top' : sin < -TOOLTIP_ALIGN_DEADZONE ? 'bottom' : 'center',
  };
};

export type ClassDiagnosticStatusReport = DiagnosticsStatusByClassReportQuery$data['classDiagnosticStatusReport'];
export type StudentReport = ClassDiagnosticStatusReport[number];

/** A "finished too fast" student row enriched with its "skills demonstrated" count. */
export type FastStudent = StudentReport & { demonstrated: number | null };

type DemonstratedSkillsReport = DiagnosticsDemonstratedSkillsQuery$data['studentsDemonstratedSkillsReport'];

/**
 * Attaches the "skills demonstrated" count to each student row.
 *
 * `studentsDemonstratedSkillsReport` is fetched once for a whole page of students and
 * comes back as its own list (the count already scoped server-side to the student's
 * current roadmap and their last diagnostic), so rows are matched up by student id.
 * Only the numeric part of the id is compared (`splitId`): both sides serialize the
 * student as a `UserNode` today, but the report reaches us through a different global-id
 * namespace than the status report, and a prefix drift would silently blank every count.
 *
 * The backend returns `0` for a student it has no usable diagnostic data for, and *omits*
 * students the caller isn't permitted to read. An omitted student therefore keeps
 * `demonstrated: null` — "we have no data" — which the row renders as "—".
 */
export const mergeDemonstrated = (
  students: StudentReport[],
  report: DemonstratedSkillsReport | null,
): FastStudent[] => {
  const countByStudentId = new Map(report?.map(entry => [splitId(entry.studentId), entry.demonstratedSkills]));

  return students.map(student => ({
    ...student,
    demonstrated: countByStudentId.get(splitId(student.student.id)) ?? null,
  }));
};

/**
 * The student's latest diagnostic id (same "last = last element" convention used
 * across this feature). Null when the student has no diagnostics.
 */
export const getLatestDiagnosticId = (report: StudentReport): string | null =>
  report.student.diagnostics?.[report.student.diagnostics.length - 1]?.id ?? null;

/** Builds the dismiss identity for a student row (stored in localStorage). */
export const buildDismissedStudent = (report: StudentReport): DismissedStudent => ({
  studentId: report.student.id,
  diagnosticId: getLatestDiagnosticId(report),
});

/** Has this student (for their latest diagnostic) been dismissed from the "finished quickly" list? */
export const isStudentDismissed = (report: StudentReport, dismissed: DismissedStudent[]): boolean =>
  dismissed.some(d => d.studentId === report.student.id && d.diagnosticId === getLatestDiagnosticId(report));

// NOTE: totalActiveTime comes from the query in SECONDS, while the design expresses this
// limit in minutes. The "taking too long" thresholds live in CloserLook/closerLookHelper,
// which is shared with the diagnostics report.
export const TOO_FAST_SECONDS = 15 * 60; // < 15 min → "finished too fast"

/**
 * Was the student's *latest* diagnostic reset (retake assigned)?
 *
 * A student can have more than one diagnostic. The array isn't guaranteed to be
 * ordered, so the active/latest one is the record with the highest numeric id
 * (not the last element). When a teacher resets a diagnostic the backend flips
 * `retakeAssigned` to true, which means the submitted result no longer counts —
 * the student must start over.
 */
const isLatestDiagnosticReset = (student: StudentReport['student']): boolean => {
  const diagnostics = student.diagnostics?.filter((d): d is NonNullable<typeof d> => Boolean(d)) ?? [];
  if (!diagnostics.length) return false;

  const latest = diagnostics.reduce((max, current) =>
    Number(splitId(current.id)) > Number(splitId(max.id)) ? current : max,
  );

  return latest.retakeAssigned === true;
};

export const getStudentDiagnosticStatus = (report: StudentReport): DiagnosticStatus => {
  switch (report.diagnosticStatus) {
    case 'DIAGNOSTIC_SUBMITTED':
      // A submitted-but-reset diagnostic drops back to "not started": the old
      // result is wiped and the student has to retake it.
      return isLatestDiagnosticReset(report.student) ? DiagnosticStatus.NOT_STARTED : DiagnosticStatus.COMPLETED;
    case 'DIAGNOSTIC_STARTED':
    case 'DIAGNOSTIC_RESUMED':
      return DiagnosticStatus.IN_PROGRESS;
    // null / undefined (never launched) and any other ActionType fall through to "not started"
    default:
      return DiagnosticStatus.NOT_STARTED;
  }
};

export const filterStudentsByStatus = (
  students: ClassDiagnosticStatusReport,
  status: DiagnosticStatus,
): StudentReport[] =>
  students
    .filter(report => getStudentDiagnosticStatus(report) === status)
    .sort((a, b) => getStudentLastName(a.student).localeCompare(getStudentLastName(b.student)));

/**
 * Nobody in the report has launched the diagnostic yet (or there is no report at all),
 * which is what makes the widget render its empty state instead of the chart.
 */
export const isDiagnosticReportEmpty = (students: ClassDiagnosticStatusReport): boolean =>
  !students?.length || students.every(report => report.diagnosticStatus === null);

/**
 * Every student finished. Resolved through `getStudentDiagnosticStatus` (rather than
 * reading `diagnosticStatus` directly) so this can never disagree with the pie chart:
 * a submitted diagnostic whose retake was assigned counts as *not* completed in both.
 */
export const haveAllStudentsCompletedDiagnostic = (students: ClassDiagnosticStatusReport): boolean =>
  !!students?.length && filterStudentsByStatus(students, DiagnosticStatus.COMPLETED).length === students.length;

export const getTabIndex = (status: DiagnosticStatus) => {
  switch (status) {
    case DiagnosticStatus.COMPLETED:
      return 0;

    case DiagnosticStatus.IN_PROGRESS:
      return 1;

    case DiagnosticStatus.NOT_STARTED:
      return 2;

    default:
      return 0;
  }
};
